import { call, put, takeLatest, select } from 'redux-saga/effects';
import { request } from 'utils/request';
import { headerActions } from './slice';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { header } from 'utils/auth';
import { selectDealersList, selectDealerSelected } from 'app/pages/Header/selectors';
import { QueryClient } from '@tanstack/react-query';

// Create a query client instance
const queryClient = new QueryClient();

// Query key for dealers
const DEALERS_QUERY_KEY = ['dealers'];

export function* getDealers({ payload }: ReturnType<any>) {
  try {
    yield put(headerActions.setLoading(true));

    const forceRefresh = Boolean(payload?.forceRefresh);

    // Check if data exists in cache (unless forced)
    const cachedData = queryClient.getQueryData(DEALERS_QUERY_KEY);
    if (cachedData && !forceRefresh) {
      yield put(headerActions.requestDealersSuccess(cachedData));
      return;
    }

    const requestURL = `/api/v2/dealers`;
    const response = yield call(request, requestURL, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
    });

    if (response.statusCode === 500) {
      yield put(headerActions.requestDealersError(response));
      return yield put(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: 'Error fetching dealers list',
        }),
      );
    }

    //sorting in alphabetical order
    if (response.body?.items?.length) {
      let responseData = JSON.parse(JSON.stringify(response));

      responseData.body.items = responseData.body.items.map(item => ({
        ...item,
        dealer: item._id || "",
        site_title: item.name || ""
      })).sort((a, b) => {
        if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') {
          return 1;
        } else if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') {
          return -1;
        } else {
          return a.name.toLowerCase() > b.name.toLowerCase()
            ? 1
            : -1;
        }
      });

      // Store in cache
      queryClient.setQueryData(DEALERS_QUERY_KEY, responseData);

      yield put(headerActions.requestDealersSuccess(responseData));

      // Keep current dealer selection object in sync with the refreshed list
      const selectedDealerState = yield select(selectDealerSelected);
      const selectedDealerId =
        selectedDealerState && typeof selectedDealerState === 'object'
          ? selectedDealerState.dealer || selectedDealerState._id
          : selectedDealerState;

      if (selectedDealerId && typeof selectedDealerId === 'string') {
        const updatedSelectedDealer = responseData.body.items.find(
          (item: any) => item._id === selectedDealerId || item.dealer === selectedDealerId,
        );

        if (updatedSelectedDealer) {
          localStorage.setItem(
            'dealer',
            JSON.stringify(updatedSelectedDealer),
          );
          yield put(headerActions.selectDealer(updatedSelectedDealer));
        }
      }
    } else {
      yield put(headerActions.requestDealersError(new Error('No dealers found')));
      yield put(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'warning',
          message: 'No dealers found',
        }),
      );
    }
  } catch (err: any) {
    console.log(err);
    let errorMessage = 'Error fetching dealers list';

    if (err.response?.status === 404) {
      errorMessage = 'Dealers API endpoint not found';
    } else if (err.message === 'Server Error') {
      errorMessage = 'Server error occurred while fetching dealers';
    }

    yield put(headerActions.requestDealersError(err));
    yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: errorMessage,
      }),
    );
  } finally {
    yield put(headerActions.setLoading(false));
  }
}

export function* setDealer({ payload }: ReturnType<any>) {
  try {
    const dealersState = yield select(selectDealersList);
    const dealers = dealersState.data?.body?.items || [];

    let dealer = dealers?.find(item => item._id === payload.dealerId);

    if (dealer) {
      localStorage.setItem('dealer', JSON.stringify(dealer));
      yield put(headerActions.selectDealer(dealer));
    } else {
      if (payload.history) {
        payload.history.goBack();
        throw new Error('dealer not found');
      }
    }
  } catch (error) {
    console.log(error);
    yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: 'Could not select the dealer',
      }),
    );
  }
}

export function* headerSaga() {
  yield takeLatest(headerActions.requestDealers.type, getDealers);
  yield takeLatest(headerActions.setDealer.type, setDealer);
}
